export interface EmbedTheme {
  mode?: 'dark' | 'light';

  /**
   * Curated brand gradients on the own bubble + send button. The derivation
   * default is FLAT, so `'on'` must be sent explicitly to restore the sweep;
   * `'off'` pins a flat solid fill. Honoured by the embed's setTheme sanitizer.
   */
  gradients?: 'on' | 'off';

  // ── Core palette (drive most derived colours) ─────────────────────────
  primaryColor?: string;
  accentColor?: string;
  /** Page / chat-area background. */
  backgroundColor?: string;
  /** Surface tone used as a fallback for header / input when not overridden. */
  surfaceColor?: string;
  /** Border colour. When unset, derived from textColor opacity. */
  borderColor?: string;

  // ── Text ──────────────────────────────────────────────────────────────
  textColor?: string;
  textSecondaryColor?: string;
  /**
   * Colour for every clickable glyph inside an INCOMING bubble — links,
   * @mentions, $tickers, handle buttons. One uniform tone per side.
   */
  linkColor?: string;
  /** Same as `linkColor` but for clickable text inside an OWN bubble. */
  linkColorOwn?: string;
  /** @deprecated alias for `linkColor`; back-compat fallback. */
  mentionColor?: string;

  // ── Bubbles ───────────────────────────────────────────────────────────
  /**
   * Background colour for incoming message bubbles. Defaults to the brand
   * purple used by chat.cherry.fun (#4a1d56). Override to recolour received
   * messages without touching the surface (header / input) palette.
   */
  incomingBubbleColor?: string;
  /** Border colour for incoming bubbles. Pairs with `incomingBubbleColor`. */
  incomingBubbleBorderColor?: string;
  /**
   * Solid background colour for own (outgoing) bubbles. When set, replaces
   * the default primary→accent gradient with a flat fill.
   */
  ownBubbleColor?: string;
  /** Text colour inside own bubbles. */
  ownBubbleTextColor?: string;

  // ── Header ────────────────────────────────────────────────────────────
  /** Header background. Falls back to surfaceColor when unset. */
  headerColor?: string;
  /** Header text colour. Falls back to textColor when unset. */
  headerTextColor?: string;

  // ── Input + Send button ───────────────────────────────────────────────
  /** Background colour for the message input field. Falls back to surface. */
  inputColor?: string;
  /** Text colour inside the input field. */
  inputTextColor?: string;
  /**
   * Solid background colour for the Send button. When set, replaces the
   * default primary→accent gradient with a flat fill.
   */
  sendButtonColor?: string;
  /**
   * Send-button icon colour (paper-plane). Defaults to white. Override
   * when the button background is too light for white to remain visible.
   */
  sendButtonTextColor?: string;

  // ── Embed-like cards (token / link / group preview / reply quote) ────
  /**
   * Background for every embed-like card on OWN messages — token cards,
   * link previews, group previews, and the reply-quote bubble. Defaults
   * to a one-shade variation of `ownBubbleColor` so the card sits
   * coherently with the surrounding bubble.
   */
  ownEmbedBgColor?: string;
  /** Same as above but for incoming messages — defaults to a shade of
   *  `incomingBubbleColor`. */
  otherEmbedBgColor?: string;
  /** @deprecated alias for `otherEmbedBgColor`; kept for back-compat. */
  embedCardColor?: string;

  // ── Floating UI ───────────────────────────────────────────────────────
  /** Background for the small reply/delete actions menu next to messages. */
  messageActionsColor?: string;
  messageActionsTextColor?: string;
  tooltipColor?: string;
  tooltipTextColor?: string;
  /** Emoji-picker panel background. */
  emojiPickerColor?: string;
  /** Avatar hover-overlay tint. */
  avatarHoverColor?: string;
  /** Loading spinner colour. Defaults to primary. */
  loaderColor?: string;

  // ── Per-side message accents ──────────────────────────────────────────
  /**
   * Accent for own (outgoing) message contextual surfaces — reply bars,
   * token-card backgrounds, inline reaction badges. Default `#f54dbb`.
   */
  messageOwnAccentColor?: string;
  /** Soft variant of the own-side accent (light pink). Default `#ffb3eb`. */
  messageOwnAccentSoftColor?: string;
  /**
   * Accent for incoming message contextual surfaces. Default `#6b307d`.
   */
  messageOtherAccentColor?: string;
  /** Soft variant of the other-side accent (light purple). Default `#d6a6e3`. */
  messageOtherAccentSoftColor?: string;

  // ── Input toolbar icons (GIF / emoji / attach) ────────────────────────
  iconButtonColor?: string;
  iconButtonHoverColor?: string;

  // ── Modal overlays + danger / error tone ──────────────────────────────
  /** Backdrop colour for modals (delete-confirm, send-image). */
  modalOverlayColor?: string;
  /** Danger / error red used by destructive actions and error states. */
  dangerColor?: string;

  /** Accent for the GM (👋) counter badge in the header. Default `#FFB800`. */
  gmColor?: string;
  /** Accent for moderation role chips (MOD / OWNER). Defaults to mention. */
  roleBadgeColor?: string;

  // ── Typography ────────────────────────────────────────────────────────
  fontFamily?: string;
  fontSize?: 'sm' | 'md' | 'lg';

  // ── Backdrop blur (companion to per-surface transparency) ─────────────
  //
  // Blur amounts in px (number or numeric string). Sanitized by the embed to a
  // finite integer clamped 0–40. All optional; unset means no blur.
  /**
   * In-iframe backdrop blur (px) on the HEADER surface — frosts the transcript
   * behind a semi-transparent header. Best paired with a transparent / alpha
   * `headerColor`.
   */
  headerBlur?: number | string;
  /** In-iframe backdrop blur (px) on the COMPOSER surface. Pairs with an alpha `inputColor`. */
  inputBlur?: number | string;
  /**
   * HOST-side blur (px). Applied by the SDK to the IFRAME ELEMENT itself (a
   * cross-origin iframe cannot sample the host page from inside), frosting the
   * host page behind the whole widget. Only meaningful with a transparent / alpha
   * `backgroundColor` (overlay mode).
   */
  backgroundBlur?: number | string;
}

export interface EmbedLayout {
  showHeader?: boolean;
  headerTitle?: string;
  showMemberCount?: boolean;
  /** @deprecated Ignored by the embed runtime (its layout sanitizer drops it). */
  showAvatars?: boolean;
  /** @deprecated Ignored by the embed runtime (its layout sanitizer drops it). */
  showTimestamps?: boolean;
  /** @deprecated Ignored by the embed runtime (its layout sanitizer drops it). */
  showReactions?: boolean;
  showInput?: boolean;
  /** @deprecated Ignored by the embed runtime; size the container element instead. */
  maxHeight?: string;
}

export type SignChallengeHandler = (message: Uint8Array) => Promise<Uint8Array>;

// ── Host-provided identity ───────────────────────────────────────────────────

/**
 * A display name and/or avatar YOUR app supplies for a wallet, so the chat
 * shows your users instead of their wallet identity (a `.sol` domain, or a
 * shortened address).
 *
 * Both fields are optional and independent — supply a name only, an avatar
 * only, or both. Anything you leave out falls back to the Cherry identity.
 *
 * When PUSHED with `setUserProfiles`, fields are merged onto whatever the chat
 * already knows for that wallet: sending only `displayName` renames the person
 * and keeps their avatar. Include a field with an empty value to clear it.
 *
 * This is a VISUAL overlay inside one running embed: Cherry stores none of it,
 * the wallet remains the author of every message, and the Cherry app is
 * unaffected. Enable it per embed at portal.cherry.fun ("Who your users appear
 * as"); without that switch the iframe never asks.
 */
export interface EmbedUserProfile {
  displayName?: string;
  /** Absolute http(s) URL. `data:`/`blob:` URLs are ignored by the iframe. */
  avatarUrl?: string;
}

/** A profile plus the wallet it belongs to (search results). */
export interface EmbedUserProfileWithId extends EmbedUserProfile {
  /** Wallet address — the only user identifier the iframe ever sees. */
  id: string;
}

/**
 * Resolve profiles for a batch of wallets.
 *
 * Return a map keyed by the SAME wallet strings you were given. Map a wallet to
 * `null` (or omit it) when you don't know it — the iframe remembers that and
 * falls back to the Cherry identity instead of asking again on every render.
 *
 * Called with at most 50 wallets at a time, batched over a 16 ms window.
 */
export type ResolveUsersHandler = (
  ids: string[],
) => Promise<Record<string, EmbedUserProfile | null>>;

export interface EmbedUserSearchParams {
  /** Prefix typed by the user. Empty/absent = first page of your directory. */
  query?: string;
  /** Opaque cursor from your previous page. */
  cursor?: string;
  limit: number;
}

export interface EmbedUserSearchResult {
  users: EmbedUserProfileWithId[];
  /** Pass back to receive the next page; omit when there are no more. */
  nextCursor?: string;
}

/**
 * Search your user directory — powers @mention autocomplete over YOUR names.
 * Without it, mentions can only match the Cherry identities the room already
 * knows.
 */
export type SearchUsersHandler = (
  params: EmbedUserSearchParams,
) => Promise<EmbedUserSearchResult>;

/**
 * Detailed lookup for a single wallet. Reserved for the profile view; the
 * current embed runtime does not call it yet.
 */
export type GetUserHandler = (id: string) => Promise<EmbedUserProfile | null>;

/** Unread counters for one room, as seen by the signed-in viewer. */
export interface UnreadRoomState {
  roomId: string;
  /** Unread messages in the room. */
  unread: number;
  /**
   * Unread "someone addressed you" signals: @-mentions, replies to the
   * viewer's messages, AND reactions on them — the same signal that drives the
   * in-chat "@" badge. A bare emoji reaction therefore lights up a host dot
   * bound to this number.
   */
  mentions: number;
}

/**
 * Snapshot of the viewer's unread state, delivered by the `unreadState` event
 * and cached by `getUnreadState()`.
 */
export interface UnreadState {
  /**
   * Counters for the room this embed renders — never the viewer's other chats.
   * 0 or 1 entries today: emission is held until the room join resolves, so
   * this is empty only when there is no roomId to join or the join failed.
   * The array is future-proof for list mode.
   */
  rooms: UnreadRoomState[];
  /** Sums across `rooms`. */
  total: { unread: number; mentions: number };
}

/**
 * Embed display mode.
 *
 * - `'single'` (default) — embed is locked to the `roomId` set at construction
 *   time; room navigation inside the iframe is disabled.
 * - `'external-controlled'` — the host drives room navigation via `setRoom()`;
 *   the iframe exposes a room-list UI but defers navigation decisions to the
 *   host. The iframe emits `roomChanged` events when the user selects a room.
 * - `'list'` — the iframe renders its full room-list UI and the user can
 *   navigate freely; the host receives `roomChanged` events but does not need
 *   to call `setRoom()` itself.
 *
 * NOTE: the embed runtime currently only implements `'single'`. Passing
 * `'external-controlled'` or `'list'` yields single-room behavior until the
 * runtime ships those modes.
 */
export type EmbedMode = 'single' | 'external-controlled' | 'list';

/**
 * How the `chatBubble` launcher renders unread state.
 *
 * - `'dot'` (default) — a bare pink dot while messages are unread, upgraded to
 *   a single `@` pill once something addressed the viewer. No numbers.
 * - `'count'` — a counter pill: the unread number, `@ N` while mentions are
 *   outstanding, capped at `99+`.
 * - `'off'` — no badge at all.
 */
export type ChatBubbleBadgeMode = 'dot' | 'count' | 'off';

export interface CherryEmbedConfig {
  appId: string;
  /**
   * Mount target: a DOM element or CSS selector. Required for `inline`
   * embeds. Omit for `floating-right` / `floating-left` — the widget then
   * mounts to `document.body`.
   */
  container?: HTMLElement | string;
  token?: string;
  /** Optional wallet address. Forwarded to iframe on mount so it is
   *  available before the first `signChallenge` request arrives. */
  walletAddress?: string;
  roomId?: string;
  /**
   * Embed display mode. Controls whether the iframe shows a single room,
   * a list, or hands room navigation control to the host.
   * Defaults to `'single'`.
   */
  mode?: EmbedMode;
  theme?: EmbedTheme;
  layout?: EmbedLayout;
  position?: 'inline' | 'floating-right' | 'floating-left';
  collapsed?: boolean;
  /**
   * Render the SDK's own floating launcher button beside the widget.
   * Defaults to `false` — the host then renders its own launcher and drives
   * `show()` / `hide()` / `toggle()` itself.
   *
   * Only meaningful with `position: 'floating-right' | 'floating-left'`;
   * silently ignored for inline embeds. Pair with `collapsed: true` (the
   * recommended combo) so the widget starts closed behind the launcher.
   *
   * While enabled the SDK owns the stacking order: the button sits on top and
   * the panel drops one z-index below it. Labels are English-only (the SDK has
   * no i18n) — hosts needing localised labels should leave this off. Removed by
   * `destroy()`; a `mount()` that rejects on the ready timeout leaves the button
   * in place for the host to tear down.
   */
  chatBubble?: boolean;
  /**
   * How the `chatBubble` launcher shows unread state. Defaults to `'dot'` —
   * the badge is on as soon as `chatBubble` is, showing a bare dot for unread
   * messages and a single `@` pill for mentions. Use `'count'` for numbers
   * (`N`, `@ N`, capped at `99+`) or `'off'` for no badge.
   *
   * Ignored without `chatBubble`. The badge follows the `unreadState` event on
   * its own and clears on any viewer change; nothing to wire up.
   */
  chatBubbleBadge?: ChatBubbleBadgeMode;
  embedUrl?: string;
  /**
   * Optional wallet signing callback registered during mount before initial
   * token / walletAddress commands are sent to the iframe.
   */
  signChallengeHandler?: SignChallengeHandler;

  // ── Host-provided identity (see EmbedUserProfile) ──────────────────────────
  // Registered during mount. All three are ignored unless the embed has
  // "Who your users appear as" enabled in the portal.

  /** Answer "who are these wallets?" from your app. */
  resolveUsers?: ResolveUsersHandler;
  /** Answer @mention autocomplete from your user directory. */
  searchUsers?: SearchUsersHandler;
  /** Detailed single-user lookup (reserved; not called yet). */
  getUser?: GetUserHandler;
  /**
   * Profiles you already have at mount time — pushed straight into the iframe
   * so the first render shows your names with no round-trip.
   */
  userProfiles?: Record<string, EmbedUserProfile>;
  /**
   * Bearer token for the profile endpoint configured in the portal. Sent as
   * `Authorization: Bearer <token>` on those requests only, kept in memory,
   * never persisted. Refresh it any time with `setIdentityToken()`.
   */
  identityToken?: string;
}

export type EmbedEventMap = {
  ready: void;
  unreadCount: number;
  /**
   * Unread + mention counters for the room this embed renders. Emitted once
   * after the session loads, on every counter change, and on demand via
   * `refreshUnreadState()`. Never emitted in preview mode — an anonymous
   * visitor has nothing to catch up on.
   *
   * Counters only grow while the widget is hidden (`hide()` / `collapsed`) or
   * while the user is reading older messages; a visible chat settled at the
   * tail marks everything read, which is what zeroes `unread` — reopening
   * behind the frozen "Unread messages" divider defers that until the viewport
   * reaches the bottom. `mentions` counts @-mentions, replies to the viewer and
   * reactions on the viewer's messages; it clears on its own boundary (the
   * in-chat "@" badge), so it outlives the reopen.
   */
  unreadState: UnreadState;
  message: { roomId: string; senderId: string; timestamp: number };
  authStateChange: boolean;
  /**
   * NOTE: not currently emitted by the embed runtime — the iframe
   * re-establishes sessions silently from a rotating refresh token when it
   * loads. Kept for forward compatibility.
   */
  tokenExpired: void;
  /**
   * NOTE: not currently emitted by the embed runtime. Kept for forward
   * compatibility.
   */
  error: { code: string; message: string };
  /**
   * Iframe requests host to initiate sign-in / wallet connect.
   * Emitted when the user clicks send/react in preview (read-only) mode,
   * or clicks an explicit "Connect wallet" CTA inside the iframe.
   * Host should respond by triggering wallet.connect and calling
   * chat.setWalletAddress(addr) (and chat.setToken(token) for
   * backend-token auth).
   */
  walletConnectRequested: void;
  /**
   * Iframe transitioned into preview-mode (room metadata + read-only messages,
   * no JWT yet). Useful for host UI to show a "Sign in" prompt nearby.
   */
  preview: { visible: boolean; gated: boolean };
  /**
   * Emitted by the iframe whenever the active room changes.
   *
   * In `'external-controlled'` mode the host drives navigation via
   * `setRoom()` and should listen to this event to keep its own state in
   * sync. In `'list'` mode the user navigates freely and the host can use
   * this event as a notification. In `'single'` mode this event is not
   * expected to fire unless the server overrides the mode.
   *
   * NOTE: not currently emitted by the embed runtime — only `'single'`
   * mode is implemented today, so this event never fires. Kept for
   * forward compatibility.
   */
  roomChanged: { roomId: string };
};

// Bridge protocol messages (host <-> iframe)
export interface BridgeMessage {
  type: 'cherry:cmd' | 'cherry:event' | 'cherry:request' | 'cherry:response';
}

export interface BridgeCommand extends BridgeMessage {
  type: 'cherry:cmd';
  method: string;
  params?: Record<string, unknown>;
}

export interface BridgeEvent extends BridgeMessage {
  type: 'cherry:event';
  event: string;
  data?: unknown;
}

// ---- Request/response (iframe→host direction) ----

/**
 * Concrete params for signChallenge requests sent by the iframe.
 * `message` is a base64-encoded byte array for the host wallet to sign.
 * Extends `Record<string, unknown>` so it is assignable to `BridgeRequest.params`.
 */
export interface SignChallengeParams extends Record<string, unknown> {
  message: string; // base64-encoded Uint8Array
}

/**
 * Successful signChallenge result returned to the iframe.
 * `signature` is the base64-encoded Ed25519 signature bytes.
 */
export interface SignChallengeResult {
  signature: string; // base64-encoded Uint8Array
}

/**
 * Typed request methods that the iframe may send to the host.
 * Extend this union when new request methods are added.
 *
 * The `users.*` trio is host-provided identity: the IFRAME pulls, because only
 * it knows which wallets are on screen. The reverse direction (the host telling
 * the iframe what changed) travels as commands — `setUserProfiles()` /
 * `invalidateUserProfiles()`.
 */
export type BridgeRequestMethod =
  | 'signChallenge'
  | 'users.resolve'
  | 'users.search'
  | 'users.get';

export interface BridgeRequest extends BridgeMessage {
  type: 'cherry:request';
  /** Unique correlation id — the matching `cherry:response` must carry the same id. */
  id: string;
  method: BridgeRequestMethod;
  params?: Record<string, unknown>;
}

/** Specific shape for signChallenge requests. */
export interface SignChallengeRequest extends BridgeMessage {
  type: 'cherry:request';
  id: string;
  method: 'signChallenge';
  params: SignChallengeParams;
}

export interface BridgeResponse extends BridgeMessage {
  type: 'cherry:response';
  /** Must match the `id` of the originating `cherry:request`. */
  id: string;
  result?: unknown;
  error?: { code: string; message: string };
}

/** Specific shape for successful signChallenge responses. */
export interface SignChallengeResponse extends BridgeMessage {
  type: 'cherry:response';
  id: string;
  result: SignChallengeResult;
}

/** Type guard: narrows a BridgeRequest to a SignChallengeRequest. */
export function isSignChallengeRequest(req: BridgeRequest): req is SignChallengeRequest {
  return (
    req.method === 'signChallenge' &&
    req.params !== undefined &&
    typeof (req.params as Record<string, unknown>)['message'] === 'string'
  );
}
